import { Grid, Paper, Box } from "@mui/material";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import {
  getStudyInfo,
  resetStudy,
  updateStudyExports,
} from "../../../redux/actions/study";
import { store } from "../../../store";
import { DataGrid } from "@mui/x-data-grid";
import { LinearProgress } from "@mui/material";
import { FormControlLabel } from "@mui/material";
import { Checkbox } from "@mui/material";
import { NoRowOverlay } from "../../table/NoRowOverlay";
import { Button } from "@mui/material";
import { TextField } from "@mui/material";
import dayjs from "dayjs";
import {
  getProtocols,
  addCenterToProtocol,
  deleteProtocol,
  getCentersForProtocol,
  removeCenterFromProtocol,
  updateProtocolStartDate,
} from "../../../services/protocolService";
import Swal from "sweetalert2";
import {
  showErrorAlert,
  showInfoAlert,
  showSuccessAlert,
} from "../../ui/utils/AlertUtils";
import { useTranslation } from "react-i18next";
import NewProtocolDialog from "../../ui/dialogs/NewProtocolDialog";
import EditStartDateDialog from "../../ui/dialogs/EditStartDateProtocol";
import CentersForProtocolDialog from "./center/CentersForProtocolDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import {
  createVisit,
  deleteVisit,
  getVisitsByStudy,
  updateVisit,
} from "../../../redux/actions/visit";

export const ScreenStudyInfo = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const studyId = location.state?.studyId;
  const study = useSelector((state) => state.study.study);
  const isLoading = useSelector((state) => state.study.isLoading);
  const visits = useSelector((state) => state.visit.visits);
  const sponsorManagers = study?.sponsors?.filter(
    (user) => user.role !== "monitor"
  );

  const protocols = study?.protocols;
  const centers = useSelector((state) => state.center.centers);
  const [hasExports, setHasExports] = React.useState(
    study?.hasExports || false
  );
  const [exportPath, setExportPath] = React.useState(study?.exportPath || "");
  const [centersDialogOpen, setCentersDialogOpen] = React.useState(false);
  const [dialog, setDialog] = React.useState({
    open: false,
    date: null,
    protocolId: null,
  });
  const [selectedCenters, setSelectedCenters] = React.useState([]);
  const [initiallySelectedCenters, setInitiallySelectedCenters] =
    React.useState([]);
  const [removedCenters, setRemovedCenters] = React.useState([]);
  const [currentProtocolId, setCurrentProtocolId] = React.useState(null);
  const [selectedProtocol, setSelectedProtocol] = React.useState(null);
  const [, setAddedCenters] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const updated = useSelector((state) => state.study.updatedStudy);
  const [protocolChanged, setProtocolChanged] = React.useState(false);
  const monitors = study?.Centers
    ? study.Centers.reduce((acc, center) => {
        const newAcc = [...acc];
        center.monitors.forEach((monitor) => {
          const existingMonitor = newAcc.find((m) => m.id === monitor.id);
          if (existingMonitor) {
            existingMonitor.centers.push({
              id: center.id,
              centerNumber: center.centerNumber,
            });
          } else {
            newAcc.push({
              id: monitor.id,
              name: monitor.name,
              firstname: monitor.firstname,
              email: monitor.email,
              phoneNumber: monitor.phoneNumber,
              centers: [{ id: center.id, centerNumber: center.centerNumber }],
            });
          }
        });
        return newAcc;
      }, [])
    : [];

  const handleConfirmExports = async () => {
    try {
      if (hasExports && !exportPath.trim()) {
        showErrorAlert(t("errorExportPath"));
        return;
      }

      const body = {
        hasExports,
        exportPath,
      };
      store.dispatch(updateStudyExports(studyId, body));
      showSuccessAlert(t("exportsUpdated"));
    } catch (error) {
      console.error("Failed to update exports:", error);
      showErrorAlert(t("failedToUpdateExports"));
    }
  };

  const handleEditVisit = async (visitId) => {
    const visitToEdit = visits.find((visit) => visit.id === visitId);

    if (!visitToEdit) {
      showErrorAlert(t("visitNotFound"));
      return;
    }

    const { value: newName } = await Swal.fire({
      title: t("editVisit"),
      input: "text",
      inputLabel: t("visitName"),
      inputValue: visitToEdit.name,
      inputPlaceholder: t("enterVisitName"),
      showCancelButton: true,
      confirmButtonText: t("save"),
      cancelButtonText: t("cancel"),
    });

    if (newName && newName !== visitToEdit.name) {
      try {
        await store.dispatch(updateVisit({ id: visitId, name: newName }));
        showSuccessAlert(t("visitUpdatedSuccess"));
      } catch (error) {
        console.error("Failed to update visit:", error);
        showErrorAlert(t("visitUpdateFailed"));
      }
    } else if (!newName) {
      console.warn(
        "Visit name update was canceled or no new name was provided."
      );
    }
  };

  const handleDeleteVisit = (visitId) => {
    Swal.fire({
      title: t("deleteVisit"),
      text: t("deleteVisitConfirmation"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("delete"),
      cancelButtonText: t("cancel"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await store.dispatch(deleteVisit(visitId));
          showSuccessAlert(t("visitDeletedSuccess"));
        } catch (error) {
          console.error("Failed to delete visit:", error);
          showErrorAlert(t("visitDeleteFailed"));
        }
      }
    });
  };

  const handleCreateVisit = async () => {
    const { value: visitName } = await Swal.fire({
      title: t("createNewVisit"),
      input: "text",
      inputLabel: t("visitName"),
      inputPlaceholder: t("visitName"),
      showCancelButton: true,
      confirmButtonText: t("create"),
      cancelButtonText: t("cancel"),
    });

    if (visitName) {
      try {
        console.log(
          "Creating visit with name:",
          visitName,
          "and studyId:",
          studyId
        );
        const newVisit = await store.dispatch(
          createVisit({ name: visitName, studyId })
        );
        showSuccessAlert(t("visitCreatedSuccess"));
        console.log("Created visit:", newVisit);
      } catch (error) {
        console.error("Failed to create visit:", error);
        showErrorAlert(t("visitCreateFailed"));
      }
    } else {
      console.warn("Visit name is required but was not provided.");
    }
  };

  const handleConfirmDate = async () => {
    try {
      await updateProtocolStartDate(dialog.protocolId, dialog.date);
      showSuccessAlert(t("protocolStartDateUpdatedSuccess"));
      setProtocolChanged(true);
      setDialog({ open: false, date: null, protocolId: null });
    } catch (error) {
      console.error(
        "An error occurred while updating the study start date:",
        error
      );
      showErrorAlert(t("protocolStartDateUpdateFailed"));
    }
  };

  const handleNewProtocol = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDialog({ open: false, date: null, protocolId: null });
  };

  const handleCentersDialogOpen = async (protocolId) => {
    try {
      const linkedCenters = await getCentersForProtocol(protocolId);
      const linkedCenterIds = linkedCenters.map((center) =>
        center.id.toString()
      );
      setSelectedCenters(linkedCenterIds);
      setInitiallySelectedCenters(linkedCenterIds);
    } catch (error) {
      console.error("Failed to fetch centers for protocol", error);
    }
    setCentersDialogOpen(true);
  };

  const handleCentersDialogClose = () => {
    setCentersDialogOpen(false);
  };

  const handleCheckboxChange = (event) => {
    if (event.target.checked) {
      setSelectedCenters([...selectedCenters, event.target.name]);
    } else {
      setSelectedCenters(
        selectedCenters.filter((centerId) => centerId !== event.target.name)
      );
      if (initiallySelectedCenters.includes(event.target.name)) {
        setRemovedCenters((prevRemovedCenters) => [
          ...prevRemovedCenters,
          event.target.name,
        ]);
      }
    }
  };

  const handleConfirm = async () => {
    if (selectedCenters.length === 0 && removedCenters.length === 0) {
      handleCentersDialogClose();
      showInfoAlert(t("noChangesMade"));
    } else {
      for (const centerId of selectedCenters) {
        if (!initiallySelectedCenters.includes(centerId)) {
          try {
            const response = await addCenterToProtocol(
              currentProtocolId,
              centerId
            );
            if (response) {
              setProtocolChanged(true);
              // Update the addedCenters state
              setAddedCenters((prevAddedCenters) => [
                ...prevAddedCenters,
                centerId,
              ]);
            }
          } catch (error) {
            showErrorAlert(t("failedToAddCenter"));
          }
        }
      }
      for (const centerId of removedCenters) {
        removeCenterFromProtocol(currentProtocolId, centerId);
      }
      if (selectedCenters.length > 0 || removedCenters.length > 0) {
        showSuccessAlert(t("changesSaved"));
      }
      setSelectedCenters([]); // Clear selectedCenters
      setRemovedCenters([]); // Clear removedCenters
    }
    handleCentersDialogClose();
  };

  // Update the protocol start date
  const handleCellEditCommit = async (params) => {
    if (params.field === "startDate") {
      const newDate = params.value;
      const protocolId = params.id;

      try {
        await updateProtocolStartDate(protocolId, newDate);
        showSuccessAlert(t("protocolStartDateUpdatedSuccess"));
      } catch (error) {
        console.error(
          "An error occurred while updating the study start date:",
          error
        );
        showErrorAlert(t("protocolStartDateUpdateFailed"));
      }
    }
  };

  // cell to add Centers to protocol
  const renderEditCell = (params) => {
    const onClick = () => {
      const protocolId = params.row.id;
      setCurrentProtocolId(protocolId);
      handleCentersDialogOpen(protocolId);

      const selectedProtocol = protocols.find(
        (protocol) => protocol.id === protocolId
      );
      setSelectedProtocol(selectedProtocol); // Update the state
    };

    return (
      <Box display="flex" justifyContent="center">
        <Button variant="contained" color="primary" onClick={onClick}>
          {t("manageCenters")}
        </Button>
      </Box>
    );
  };
  //sponsorManagers
  const columns = [
    { field: "name", headerName: t("name"), flex: 1 },
    { field: "firstname", headerName: t("firstname"), flex: 1 },
    { field: "email", headerName: t("email"), flex: 1 },
    { field: "phoneNumber", headerName: t("phoneNumber"), flex: 1 },
  ];

  const monitorColumns = [
    ...columns,
    {
      field: "centers",
      headerName: t("centers"),
      flex: 1,
      valueGetter: (params) =>
        params.row.centers.map((center) => center.centerNumber).join(", "),
    },
  ];

  const protocolColumns = [
    { field: "reference", headerName: t("reference"), flex: 0.4 },
    {
      field: "startDate",
      headerName: t("startDate"),
      flex: 0.4,
      renderCell: (params) => (
        <div
          onDoubleClick={() =>
            setDialog({ open: true, date: params.value, protocolId: params.id })
          }
        >
          {dayjs(params.value).format("DD/MM/YYYY")}
        </div>
      ),
    },
    {
      field: "edit",
      headerName: "",
      sortable: false,
      width: 250,
      disableClickEventBubbling: true,
      renderCell: renderEditCell,
    },
    {
      field: "delete",
      headerName: "",
      sortable: false,
      width: 150,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        const onClick = async () => {
          const id = params.row.id;
          Swal.fire({
            title: t("warningAlert"),
            text: t("deleteProtocolText"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#1e3179",
            confirmButtonText: t("deleteConfirmButton"),
            cancelButtonText: t("cancel"),
          }).then(async (result) => {
            if (result.isConfirmed) {
              try {
                const protocolDeleted = await deleteProtocol(id);
                if (protocolDeleted) {
                  showSuccessAlert(t("deleteProtocolSuccess"));
                  setProtocolChanged(true);
                }
              } catch (error) {
                showErrorAlert(t("deleteProtocolFailed"));
                console.error("Failed to delete protocol", error);
              }
            }
          });
        };

        return (
          <Tooltip title={t("delete")}>
            <Button onClick={onClick} color="secondary">
              <DeleteIcon />
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  React.useEffect(() => {
    if (protocolChanged) {
      setProtocolChanged(false);
    }
    if (updated) {
      store.dispatch(resetStudy());
    }
    store.dispatch(getStudyInfo(studyId));
    store.dispatch(getVisitsByStudy(studyId));
  }, [studyId, protocolChanged, updated]);

  React.useEffect(() => {
    if (study) {
      setExportPath(study.exportPath || "");
      setHasExports(study.hasExports || false);
    }
  }, [study]);

  return (
    <Grid item xs={12}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "auto",
        }}
      >
        <div style={{ width: "100%" }}>
          <Box mx={4} mt={8}>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label={t("cdlStudyCode")}
                  value={study?.cdlStudyCode || ""}
                  inputProps={{
                    style: { textAlign: "center" },
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label={t("sponsorStudyCode")}
                  value={study?.sponsorStudyCode || ""}
                  inputProps={{
                    style: { textAlign: "center" },
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Sponsor"
                  value={study?.sponsor?.name || ""}
                  inputProps={{
                    style: { textAlign: "center" },
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          <Box m={4}>
            <TextField
              value="SPONSOR MANAGERS"
              InputProps={{
                readOnly: true,
              }}
              inputProps={{
                style: { textAlign: "center" },
              }}
              fullWidth
            />
            {!isLoading && sponsorManagers && sponsorManagers?.length > 0 ? (
              <DataGrid
                disableRowSelectionOnClick
                slots={{
                  noRowsOverlay: NoRowOverlay,
                  loadingOverlay: LinearProgress,
                }}
                rows={sponsorManagers}
                columns={columns}
                loading={isLoading}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 10 },
                  },
                }}
                pageSizeOptions={[10, 20]}
                checkboxSelection
                hideFooter
              />
            ) : (
              <div style={{ display: "flex", justifyContent: "center" }}>
                {t("noSponsorManagers")}
              </div>
            )}
          </Box>

          <Box m={4}>
            <TextField
              value={t("monitors").toUpperCase()}
              InputProps={{
                readOnly: true,
              }}
              inputProps={{
                style: { textAlign: "center" },
              }}
              fullWidth
            />
            {!isLoading && monitors && monitors?.length > 0 ? (
              <DataGrid
                disableRowSelectionOnClick
                slots={{
                  noRowsOverlay: NoRowOverlay,
                  loadingOverlay: LinearProgress,
                }}
                rows={monitors}
                columns={monitorColumns}
                loading={isLoading}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 10 },
                  },
                }}
                pageSizeOptions={[10, 20]}
                checkboxSelection
                hideFooter
              />
            ) : (
              <div style={{ display: "flex", justifyContent: "center" }}>
                {t("noMonitors")}
              </div>
            )}
          </Box>

          <Box m={4}>
            <TextField
              value={t("exports").toUpperCase()}
              InputProps={{
                readOnly: true,
              }}
              inputProps={{
                style: { textAlign: "center" },
              }}
              fullWidth
            />
            <Box display="flex" alignItems="center" mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasExports}
                    onChange={(e) => setHasExports(e.target.checked)}
                    color="primary"
                  />
                }
                label={t("hasExports")}
                sx={{
                  width: "20%",
                  marginRight: "16px",
                }}
              />
              <TextField
                label={t("exportPath")}
                value={exportPath}
                onChange={(e) => setExportPath(e.target.value)}
                fullWidth
                disabled={!hasExports}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmExports}
                disabled={isLoading}
              >
                {t("confirm")}
              </Button>
            </Box>
          </Box>

          {/* ---------------------------------------------- */}
          {/* protocol list  */}
          {/* ---------------------------------------------- */}
          <Box m={4}>
            <TextField
              value={t("protocols").toUpperCase()}
              InputProps={{
                readOnly: true,
              }}
              inputProps={{
                style: { textAlign: "center" },
              }}
              fullWidth
            />
            {!isLoading && protocols && protocols?.length > 0 ? (
              <DataGrid
                disableRowSelectionOnClick
                slots={{
                  noRowsOverlay: NoRowOverlay,
                  loadingOverlay: LinearProgress,
                }}
                rows={protocols}
                columns={protocolColumns}
                loading={isLoading}
                pageSizeOptions={[10, 20]}
                hideFooter
                onCellEditCommit={handleCellEditCommit}
              />
            ) : (
              <div style={{ display: "flex", justifyContent: "center" }}>
                {t("noAdditionalProtocols")}
              </div>
            )}

            {/* EDIT THE START DATE OF A PROTOCOL */}
            <EditStartDateDialog
              open={dialog.open}
              handleClose={handleClose}
              dialog={dialog}
              setDialog={setDialog}
              handleConfirmDate={handleConfirmDate}
            />
            <Box display="flex" justifyContent="center">
              {/* ADD A NEW PROTOCOL */}
              <Button
                variant="contained"
                color="primary"
                onClick={handleNewProtocol}
                style={{ padding: "16px 88px", marginTop: "16px" }}
              >
                {t("addNewProtocol")}
              </Button>
              <NewProtocolDialog
                open={open}
                handleClose={handleClose}
                setOpen={setOpen}
                setProtocolChanged={setProtocolChanged}
                studyId={studyId}
              />
            </Box>

            {/* ADD CENTERS TO A PROTOCOL  */}
            <CentersForProtocolDialog
              open={centersDialogOpen}
              handleClose={handleCentersDialogClose}
              selectedProtocol={selectedProtocol}
              centers={centers}
              selectedCenters={selectedCenters}
              handleCheckboxChange={handleCheckboxChange}
              handleConfirm={handleConfirm}
            />

            <Box mt={4}>
              <TextField
                value={t("visits").toUpperCase()}
                InputProps={{
                  readOnly: true,
                }}
                inputProps={{
                  style: { textAlign: "center" },
                }}
                fullWidth
              />

              <Grid container spacing={2} mt={2}>
                {Array.isArray(visits) &&
                  visits
                    .slice()
                    .sort((a, b) => {
                      const extractParts = (name) => {
                        const match = name.match(
                          /([A-Za-z]+)(\d+)([A-Za-z]+)?(\d+)?/
                        );
                        if (match) {
                          return [
                            match[1],
                            parseInt(match[2], 10),
                            match[3] || "",
                            parseInt(match[4], 10) || 0,
                          ];
                        }
                        return [name];
                      };

                      const partsA = extractParts(a.name);
                      const partsB = extractParts(b.name);

                      for (let i = 0; i < partsA.length; i++) {
                        if (partsA[i] < partsB[i]) return -1;
                        if (partsA[i] > partsB[i]) return 1;
                      }
                      return 0;
                    })
                    .map((visit) => (
                      <Grid item xs={3} key={visit.id}>
                        <Paper
                          elevation={2}
                          style={{ padding: "16px", textAlign: "center" }}
                        >
                          <div>{visit.name}</div>
                          <Box display="flex" justifyContent="center" mt={1}>
                            <IconButton
                              edge="end"
                              onClick={() => handleEditVisit(visit.id)}
                              title={t("edit")}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => handleDeleteVisit(visit.id)}
                              title={t("delete")}
                            >
                              <DeleteIcon color="secondary" />
                            </IconButton>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
              </Grid>

              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreateVisit}
                >
                  {t("createNewVisit")}
                </Button>
              </Box>
            </Box>
          </Box>
        </div>
      </Paper>
    </Grid>
  );
};
